import {
  addSegment,
  alcoveCornerLineFrame,
  clamp,
  DOOR_WIDTH,
  edgeItemWithDefaults,
  type EdgeName,
  type EdgeSegmentType,
  type FloorPlanEdgeSegment,
  type FloorPlanRoom,
  type LineEdgeRef,
  lineRefSegments,
  makeAlcove,
  primaryEdgeLineFrame,
  roomCornerLineFrame,
  roundToHalfInch,
} from '@kaiizen/kai-floor-plan';
import { primaryEdgeSegmentSelection } from './edgeRefs';
import type { EditorMutableState } from './editorState';
import { select } from './selection';

/** The narrowest default width (feet) for a new item on a wall, and on a line (a corner's diagonal or an alcove wall). */
const WALL_MINIMUM_WIDTH = 2, LINE_MINIMUM_WIDTH = 1.5;

/**
 * A new item's width on a span: a door is a standard 2'8" when that fits,
 * otherwise it fills the span; other kinds take a third of the span on the
 * half inch, no narrower than `minimumWidth` unless the span is.
 */
function defaultEdgeItemWidth(kind: EdgeSegmentType, spanLength: number, minimumWidth: number): number {
  if (kind === 'door') return Math.min(DOOR_WIDTH, spanLength);
  return clamp(roundToHalfInch(spanLength / 3), Math.min(minimumWidth, spanLength), spanLength);
}

/** A new item of `kind` centered on the span from `spanStart` to `spanEnd`, its position on the half inch. */
function centeredEdgeItem(kind: EdgeSegmentType, spanStart: number, spanEnd: number, minimumWidth: number, depth = 0): FloorPlanEdgeSegment {
  const spanLength = spanEnd - spanStart;
  const width = defaultEdgeItemWidth(kind, spanLength, minimumWidth);
  return edgeItemWithDefaults(makeAlcove(roundToHalfInch(spanStart + (spanLength - width) / 2), width, depth), kind, true);
}

/** A new alcove's depth on `edge`: 3', or the room's whole extent across that wall when it is shallower. */
function newAlcoveDepth(shape: FloorPlanRoom, edge: EdgeName): number {
  return Math.min(3, Math.abs(primaryEdgeLineFrame(shape, edge).perp.x) > 0.5 ? shape.width : shape.length);
}

/** The selected line an item can go on (a room or alcove corner's diagonal, or an alcove wall) and its length, or null. */
function selectedLineHost({ selection, shape }: EditorMutableState): { ref: LineEdgeRef; length: number } | null {
  if (selection.kind === 'corner') {
    const lineFrame = roomCornerLineFrame(shape, selection.corner);
    return lineFrame ? { ref: { k: 'rc', corner: selection.corner }, length: lineFrame.length } : null;
  }
  if (selection.kind === 'alcoveCorner') {
    const lineFrame = alcoveCornerLineFrame(shape, selection.edge, selection.index, selection.which);
    return lineFrame ? { ref: { k: 'ac', edge: selection.edge, storageIndex: selection.index, which: selection.which }, length: lineFrame.length } : null;
  }
  if (selection.kind === 'alcoveWall') {
    const alcove = shape[selection.edge]?.segments[selection.index];
    if (!alcove) return null;
    return {
      ref: { k: 'aw', edge: selection.edge, storageIndex: selection.index, wall: selection.wall },
      length: selection.wall === 'backEdge' ? alcove.width : alcove.depth ?? 0,
    };
  }
  return null;
}

/**
 * Insert a segment on a host line and select it canonically, so every tool
 * (dims, handles, trash icon, inspector) lights up together. `addSegment`
 * appends, so the new segment is the host's last.
 */
function insertAndSelect(state: EditorMutableState, ref: LineEdgeRef, segment: FloorPlanEdgeSegment): void {
  state.shape = addSegment(state.shape, ref, segment);
  const segmentIndex = lineRefSegments(state.shape, ref).length - 1;
  select(state, ref.k === 'pe' ? primaryEdgeSegmentSelection(state.shape, ref.edge, segmentIndex) : { kind: 'lineSeg', ref, si: segmentIndex });
}

/**
 * The ⊕ menu's add, on both platforms: closes the menu and adds an item of
 * `kind`, centered on the selected host, then selects it. On a wall the item
 * goes in the gap under the pointer (`edgeGap`). A line host (a room or
 * alcove corner's diagonal, or an alcove wall) takes doors, windows and
 * openings but never an alcove. A gap or line too short for an item adds
 * nothing.
 */
export function addEdgeItem(state: EditorMutableState, kind: EdgeSegmentType): void {
  state.addMenu = null;
  if (state.selection.kind === 'edge' && state.edgeGap) {
    const { edge, gs, ge } = state.edgeGap;
    if (ge - gs <= 0.05) return;
    const depth = kind === 'alcove' ? newAlcoveDepth(state.shape, edge) : 0;
    insertAndSelect(state, { k: 'pe', edge }, centeredEdgeItem(kind, gs, ge, WALL_MINIMUM_WIDTH, depth));
    return;
  }
  const host = kind === 'alcove' ? null : selectedLineHost(state);
  if (!host || host.length < 0.1) return;
  insertAndSelect(state, host.ref, centeredEdgeItem(kind, 0, host.length, LINE_MINIMUM_WIDTH));
}
